import React, { useRef, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  LineController,
  Legend,
  BarController,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useTheme } from "../contexts/ThemeContext.jsx";
import { calculateBudgetEntryStatus } from "../services/azureBudgetService";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend
);

const FinanzChart = ({ bookings, budgetProjections = [] }) => {
  const chartRef = useRef();
  const { getComputedThemeValue } = useTheme();

  // Verarbeite historische Buchungsdaten
  const processHistoricalData = () => {
    const monthlyData = {};
    const sortedBookings = [...bookings].sort((a, b) => a.Valuta - b.Valuta);

    let runningBankSaldo = 0;

    sortedBookings.forEach((booking) => {
      const monthKey = booking.Valuta.toISOString().slice(0, 7);

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          Einnahmen: 0,
          Material: 0,
          Personal: 0,
          Betrieb: 0,
          Ausgaben: 0,
          Cashflow: 0,
          BankSaldo: 0,
        };
      }

      if (monthKey === "2025-03") {
        console.log("check", booking);
      }

      if (booking.Konto === booking.Gegenkonto) {
        return null;
      } else if (booking.Konto === 1020) {
        runningBankSaldo += booking.Betrag;

        // Nur Einnahme wenn Gegenkonto ein Erlöskonto ist
        if (booking.Gegenkonto >= 3000 && booking.Gegenkonto < 4000) {
          monthlyData[monthKey].Einnahmen += booking.Betrag;
          monthlyData[monthKey].Cashflow += booking.Betrag;
        }
        // Weitere Fälle wie Debitorenzahlungen
        else if (booking.Gegenkonto === 1100) {
          monthlyData[monthKey].Einnahmen += booking.Betrag;
          monthlyData[monthKey].Cashflow += booking.Betrag;
        }
      } else if (booking.Gegenkonto === 1020) {
        runningBankSaldo -= booking.Betrag;

        // Kategorisiere Ausgaben
        if (booking.Konto >= 4000 && booking.Konto < 5000) {
          monthlyData[monthKey].Material += booking.Betrag;
          monthlyData[monthKey].Ausgaben += booking.Betrag;
          monthlyData[monthKey].Cashflow -= booking.Betrag;
        } else if (
          (booking.Konto >= 5000 && booking.Konto < 6000) ||
          booking.Konto === 1091
        ) {
          monthlyData[monthKey].Personal += booking.Betrag;
          monthlyData[monthKey].Ausgaben += booking.Betrag;
          monthlyData[monthKey].Cashflow -= booking.Betrag;
        } else if (booking.Konto >= 6000) {
          monthlyData[monthKey].Betrieb += booking.Betrag;
          monthlyData[monthKey].Ausgaben += booking.Betrag;
          monthlyData[monthKey].Cashflow -= booking.Betrag;
        }
        // Kreditorenzahlungen (1000-1099 ausser Bank)
        else if (
          booking.Konto >= 1000 &&
          booking.Konto < 1100 &&
          booking.Konto !== 1020
        ) {
          monthlyData[monthKey].Ausgaben += booking.Betrag;
          monthlyData[monthKey].Cashflow -= booking.Betrag;
        }
      }

      monthlyData[monthKey].BankSaldo = runningBankSaldo;
    });

    return monthlyData;
  };

  // Verarbeite Budget-Projektionen
  const processBudgetProjections = () => {
    const monthlyProjections = {};

    budgetProjections.forEach((projection) => {
      const date = new Date(projection.date);
      if (isNaN(date)) return;

      const monthKey = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;

      if (!monthlyProjections[monthKey]) {
        monthlyProjections[monthKey] = {
          BudgetEinnahmen: 0,
          BudgetEinnahmenOverdue: 0, // NEU: Überfällige Einnahmen
          BudgetAusgaben: 0,
          BudgetBank: 0,
          isProjection: true,
        };
      }

      const betrag = projection.brutto_betrag || projection.betrag;

      // NEU: Berechne Status für Einnahmen
      const status = projection.typ === "Einnahme" ? calculateBudgetEntryStatus(projection) : null;
      const isOverdue = status === "overdue";

      if (projection.typ === "Einnahme") {
        if (isOverdue) {
          monthlyProjections[monthKey].BudgetEinnahmenOverdue += betrag;
        } else {
          monthlyProjections[monthKey].BudgetEinnahmen += betrag;
        }
        monthlyProjections[monthKey].BudgetBank += betrag;
      } else {
        monthlyProjections[monthKey].BudgetAusgaben += betrag;
        monthlyProjections[monthKey].BudgetBank -= betrag;
      }
    });

    return monthlyProjections;
  };

  // Kombiniere historische Daten und Projektionen
  const getCombinedData = () => {
    const historicalData = processHistoricalData();
    const projectionData = processBudgetProjections();

    // Kombiniere alle Monate
    const allMonths = new Set([
      ...Object.keys(historicalData),
      ...Object.keys(projectionData),
    ]);

    const combined = {};

    allMonths.forEach((month) => {
      combined[month] = {
        Einnahmen: historicalData[month]?.Einnahmen || 0,
        Material: historicalData[month]?.Material || 0,
        Personal: historicalData[month]?.Personal || 0,
        Betrieb: historicalData[month]?.Betrieb || 0,
        BankSaldo: historicalData[month]?.BankSaldo || 0,
        BudgetEinnahmen: projectionData[month]?.BudgetEinnahmen || 0,
        BudgetEinnahmenOverdue: projectionData[month]?.BudgetEinnahmenOverdue || 0, // NEU
        BudgetAusgaben: projectionData[month]?.BudgetAusgaben || 0,
        BudgetBank: projectionData[month]?.BudgetBank || 0,
        isHistorical: !!historicalData[month],
        isProjection: !!projectionData[month],
        Cashflow: historicalData[month]?.Cashflow || 0,
      };
    });

    return combined;
  };

  // Kumulativen Bank-Saldo berechnen
  const calculateCumulativeBankSaldo = (combinedData) => {
    const sortedMonths = Object.keys(combinedData).sort();
    const currentMonth = new Date().toISOString().slice(0, 7);
    let cumulativeHistorical = 0;
    let lastHistoricalSaldo = 0;

    const result = {};

    sortedMonths.forEach((month) => {
      const data = combinedData[month];

      // Historischer kumulativer Saldo
      if (month <= currentMonth && data.isHistorical) {
        cumulativeHistorical = data.BankSaldo;
        lastHistoricalSaldo = cumulativeHistorical;
        result[month] = {
          ...data,
          BankCumulative: cumulativeHistorical,
          BudgetBankCumulative: null,
        };
      } else {
        // Für Projektions-Monate: Starte mit letztem historischen Saldo
        lastHistoricalSaldo += data.BudgetBank;
        result[month] = {
          ...data,
          BankCumulative: null, // Keine historischen Daten mehr
          BudgetBankCumulative: lastHistoricalSaldo,
        };
      }
    });

    return { result, lastHistoricalSaldo, sortedMonths, currentMonth };
  };

  const prepareChartData = () => {
    const combinedData = getCombinedData();
    const {
      result: cumulativeData,
      lastHistoricalSaldo,
      sortedMonths,
      currentMonth,
    } = calculateCumulativeBankSaldo(combinedData);

    const labels = sortedMonths.map((month) => {
      const [year, monthNum] = month.split("-");
      const date = new Date(year, monthNum - 1);
      const monthName = date.toLocaleDateString("de-CH", {
        month: "short",
        year: "2-digit",
      });
      const isProjection = month > currentMonth;
      return isProjection ? `${monthName} (Prognose)` : monthName;
    });

    // Finde den Index des aktuellen Monats für die Übergangs-Verbindung
    const currentMonthIndex = sortedMonths.findIndex(
      (month) => month === currentMonth
    );
    const nextMonthIndex = currentMonthIndex + 1;

    // Erstelle Budget-Prognose-Daten mit Startpunkt vom letzten historischen Monat
    const budgetPrognoseData = sortedMonths.map((month, index) => {
      if (month <= currentMonth) {
        // Für historische Monate: null (keine Anzeige)
        return null;
      } else if (index === nextMonthIndex) {
        // Erster Prognose-Monat: Starte mit letztem historischen Saldo
        const lastHistoricalIndex = currentMonthIndex;
        const lastHistoricalValue =
          lastHistoricalIndex >= 0
            ? cumulativeData[sortedMonths[lastHistoricalIndex]]?.BankCumulative
            : 0;
        return lastHistoricalValue + cumulativeData[month].BudgetBank;
      } else {
        // Nachfolgende Prognose-Monate
        return cumulativeData[month].BudgetBankCumulative;
      }
    });

    // Verbindungspunkt: Letzter historischer Punkt + erster Prognose-Punkt
    const connectionData = sortedMonths.map((month, index) => {
      if (index === currentMonthIndex) {
        // Letzter historischer Punkt
        return cumulativeData[month].BankCumulative;
      } else if (index === nextMonthIndex) {
        // Erster Prognose-Punkt
        return budgetPrognoseData[index];
      } else {
        return null;
      }
    });

    const datasets = [
      // Historische Daten
      {
        type: "bar",
        label: "Einnahmen",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isHistorical
            ? cumulativeData[month].Einnahmen
            : null
        ),
        backgroundColor: "rgba(34, 197, 94, 0.8)",
        borderColor: "rgb(34, 197, 94)",
        barThickness: 20,
        order: 1,
      },
      {
        type: "bar",
        label: "Personal",
        stack: "ausgaben",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isHistorical
            ? -cumulativeData[month].Personal
            : null
        ),
        backgroundColor: "rgba(239, 68, 68, 0.8)",
        borderColor: "rgb(239, 68, 68)",
        barThickness: 20,
        order: 1,
      },
      {
        type: "bar",
        label: "Betrieb",
        stack: "ausgaben",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isHistorical
            ? -cumulativeData[month].Betrieb
            : null
        ),
        backgroundColor: "rgba(251, 146, 60, 0.8)",
        borderColor: "rgb(251, 146, 60)",
        barThickness: 20,
        order: 1,
      },
      {
        type: "bar",
        label: "Material",
        stack: "ausgaben",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isHistorical
            ? -cumulativeData[month].Material
            : null
        ),
        backgroundColor: "rgba(168, 85, 247, 0.8)",
        borderColor: "rgb(168, 85, 247)",
        barThickness: 20,
        order: 1,
      },
      // Budget-Projektionen
      {
        type: "bar",
        label: "Budget Einnahmen",
        stack: "ausgaben",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isProjection
            ? cumulativeData[month].BudgetEinnahmen
            : null
        ),
        backgroundColor: "rgba(34, 197, 94, 0.4)",
        borderColor: "rgb(34, 197, 94)",
        barThickness: 5,
        borderWidth: 2,
        borderDash: [5, 5],
        order: 2,
      },
      // Budget Einnahmen Überfällig (NEU)
      {
        type: "bar",
        label: "Budget Einnahmen (überfällig)",
        stack: "ausgaben",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isProjection
            ? cumulativeData[month].BudgetEinnahmenOverdue
            : null
        ),
        backgroundColor: "rgba(251, 146, 60, 0.6)", // Orange für überfällige Budget-Einnahmen
        borderColor: "rgb(251, 146, 60)",
        barThickness: 5,
        borderWidth: 2,
        borderDash: [5, 5],
        order: 2,
      },
      {
        type: "bar",
        label: "Budget Ausgaben",
        data: sortedMonths.map((month) =>
          cumulativeData[month].isProjection
            ? -cumulativeData[month].BudgetAusgaben
            : null
        ),
        backgroundColor: "rgba(239, 68, 68, 0.4)",
        borderColor: "rgb(239, 68, 68)",
        barThickness: 5,
        borderWidth: 2,
        borderDash: [5, 5],
        order: 2,
      },
      // Bank-Saldo Linie (nur historisch bis aktueller Monat)
      {
        type: "line",
        label: "Bank Saldo",
        data: sortedMonths.map((month) =>
          month <= currentMonth ? cumulativeData[month].BankSaldo : null
        ),

        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 2,
        fill: false,
        tension: 0.2,
        pointRadius: 1,
        pointHoverRadius: 3,
        order: 0,
      },
      // Verbindungslinie (unsichtbar, nur für nahtlosen Übergang)
      {
        type: "line",
        label: "", // Kein Label (versteckt in Legende)
        data: connectionData,
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 2,
        borderDash: [4, 4],
        fill: false,
        tension: 0.2,
        pointRadius: 0, // Keine Punkte anzeigen
        pointHoverRadius: 0,
        showLine: true,
        spanGaps: false,
        order: 0,
        legend: {
          display: false, // Verstecke in Legende
        },
      },
      // Budget Bank-Prognose (ab nächstem Monat mit Dreiecken)
      {
        type: "line",
        label: "Budget Bank Prognose",
        data: budgetPrognoseData,
        borderColor: "rgb(59, 130, 246)",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        borderWidth: 2,
        borderDash: [4, 4],
        fill: false,
        tension: 0.2,
        pointRadius: 0,
        pointHoverRadius: 1,
        order: 0,
      },
    ];

    return { labels, datasets };
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: "Finanzübersicht",
        font: { size: 16, weight: "bold" },
        padding: { bottom: 20 },
        color: getComputedThemeValue("--text-color"),
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          usePointStyle: true,
          padding: 15,
        },
      },
      tooltip: {
        backgroundColor: getComputedThemeValue("--project-color"),
        titleColor: getComputedThemeValue("--text-color"),
        bodyColor: getComputedThemeValue("--text-color"),
        borderColor: getComputedThemeValue("--project-color"),
        borderWidth: 1,
        callbacks: {
          title: (tooltipItems) => {
            const month = tooltipItems[0].label.replace(" (Prognose)", "");
            const isProjection = tooltipItems[0].label.includes("(Prognose)");
            return `${month} ${isProjection ? "(Prognose)" : "(Ist)"}`;
          },
          label: (context) => {
            const label = context.dataset.label;
            const value = context.parsed.y;
            const currency = new Intl.NumberFormat("de-CH", {
              style: "currency",
              currency: "CHF",
            }).format(Math.abs(value));

            return `${label}: ${value < 0 ? "-" : ""}${currency}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: false,
        grid: {
          color: getComputedThemeValue("--text-color"),
        },
        ticks: {
          callback: (value) => {
            return new Intl.NumberFormat("de-CH", {
              style: "currency",
              currency: "CHF",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
      },
    },
  };

  const chartData = prepareChartData();

  return (
    <div className="finanz-chart-wrapper">
      <Chart ref={chartRef} type="bar" data={chartData} options={options} />

      {budgetProjections.length > 0 && (
        <div className="chart-info">
          <p className="chart-legend">
            Historische Daten | Budget-Planung | Saldo-Entwicklung
          </p>
          <p className="chart-note">
            Prognose-Monate sind mit "(Prognose)" gekennzeichnet und basieren
            auf Ihren Budget-Eingaben
          </p>
        </div>
      )}
    </div>
  );
};

export default FinanzChart;
